"use client";

import type * as React from "react";

import { cn } from "./lib/utils";

function Table({ className, ...props }: React.ComponentProps<"table">) {
	return (
		<div
			data-slot="table-container"
			className="relative w-full overflow-x-auto"
		>
			<table
				data-slot="table"
				className={cn("w-full caption-bottom text-sm", className)}
				{...props}
			/>
		</div>
	);
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
	return (
		<thead
			data-slot="table-header"
			className={cn("[&_tr]:border-b", className)}
			{...props}
		/>
	);
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
	return (
		<tbody
			data-slot="table-body"
			className={cn("[&_tr:last-child]:border-0", className)}
			{...props}
		/>
	);
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
	return (
		<tfoot
			data-slot="table-footer"
			className={cn(
				"bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
				className,
			)}
			{...props}
		/>
	);
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
	return (
		<tr
			data-slot="table-row"
			className={cn(
				"hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
				className,
			)}
			{...props}
		/>
	);
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
	return (
		<th
			data-slot="table-head"
			className={cn(
				"text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
				className,
			)}
			{...props}
		/>
	);
}

function TableCell({
	className,
	header,
	...props
}: React.ComponentProps<"td"> & { header?: boolean }) {
	void header;
	return (
		<td
			data-slot="table-cell"
			className={cn(
				"p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
				className,
			)}
			{...props}
		/>
	);
}

function TableCaption({
	className,
	...props
}: React.ComponentProps<"caption">) {
	return (
		<caption
			data-slot="table-caption"
			className={cn("text-muted-foreground mt-4 text-sm", className)}
			{...props}
		/>
	);
}

export type TableColumn<T = unknown> = {
	key: string;
	title: string;
	render: (value: T, record: T) => React.ReactNode;
	width?: string;
	className?: string;
	align?: "left" | "right" | "center";
};

interface DataTableProps<T = unknown> {
	columns: TableColumn<T>[];
	data: T[];
	loading?: boolean;
	pagination?: {
		current: number;
		pageSize: number;
		total: number;
		onChange: (page: number) => void;
	};
	className?: string;
}

function DataTable<T = unknown>({
	columns,
	data,
	loading = false,
	pagination,
	className,
}: DataTableProps<T>) {
	if (loading) {
		return (
			<div className={cn("space-y-4", className)}>
				<div className="animate-pulse">
					<div className="h-4 bg-muted rounded w-3/4 mb-2" />
					<div className="h-4 bg-muted rounded w-1/2" />
				</div>
			</div>
		);
	}

	return (
		<div className={cn("space-y-4", className)}>
			<Table>
				<TableHeader>
					<TableRow>
						{columns.map((column) => (
							<TableHead
								key={column.key}
								className={column.className}
								style={column.width ? { width: column.width } : undefined}
							>
								{column.title}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{data.map((record, index) => (
						<TableRow
							key={`row-${(record as Record<string, unknown>)?.id ?? index}`}
						>
							{columns.map((column) => (
								<TableCell key={column.key} className={column.className}>
									{column.render(record, record)}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>

			{pagination && (
				<div className="flex items-center justify-between">
					<div className="text-sm text-muted-foreground">
						Showing {(pagination.current - 1) * pagination.pageSize + 1} to{" "}
						{Math.min(
							pagination.current * pagination.pageSize,
							pagination.total,
						)}{" "}
						of {pagination.total} entries
					</div>
					<div className="flex items-center space-x-2">
						<button
							type="button"
							onClick={() => pagination.onChange(pagination.current - 1)}
							disabled={pagination.current === 1}
							className="px-3 py-1 text-sm border rounded disabled:opacity-50"
						>
							Previous
						</button>
						<span className="text-sm">
							Page {pagination.current} of{" "}
							{Math.ceil(pagination.total / pagination.pageSize)}
						</span>
						<button
							type="button"
							onClick={() => pagination.onChange(pagination.current + 1)}
							disabled={
								pagination.current ===
								Math.ceil(pagination.total / pagination.pageSize)
							}
							className="px-3 py-1 text-sm border rounded disabled:opacity-50"
						>
							Next
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export {
	DataTable,
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
};
